import { ReactComponent as IconChevronDown } from '@/assets/svgs/chevron-down.svg'
import { ReactComponent as IconDownloadAllError } from '@/assets/svgs/download-all-error-message.svg'
import { ReactComponent as IconDownloadFolder } from '@/assets/svgs/download-all-white.svg'
import { ReactComponent as IconDownload } from '@/assets/svgs/download-all-folder.svg'
import { ReactComponent as IconEclip } from '@/assets/svgs/eclip-large.svg'
import { ReactComponent as IconGridView } from '@/assets/svgs/grid-view.svg'
import { ReactComponent as IconSearch } from '@/assets/svgs/icon-search.svg'
import { ReactComponent as IconTableView } from '@/assets/svgs/table-view.svg'
import Button from "@/components/Button"
import Dropdown from '@/components/Dropdown/index.js'
import InputSuffixIcon from '@/components/InputSuffixIcon'
import Portal from '@/components/Portal/index.js'
import { sortByOptions } from '@/constants/index'
import { ToastContext } from '@/context/toast/index'
import useModal from "@/hooks/use-modal/index"
import usePackageFolders from '@/hooks/use-package-folder/index.js'
import usePackages from '@/hooks/use-packages/index.js'
import usePage from '@/hooks/use-page/index.js'
import usePaginating from '@/hooks/use-paginating/index.js'
import { useLocation, useParams } from '@/lib/router-dom/index.js'
import api from "@/services/api/messages.js"
import rem from '@/utils/rem.js'
import PropTypes from 'prop-types'
import { useContext, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import DownloadAllDataModal from "./components/download-all-data-modal.jsx"
import PackageCard from './components/package-card.jsx'
import PackageDataTable from './components/package-data-table.jsx'
import FilterDropDown from './components/package-filter-dropdown.jsx'
import Search from '@/componentsDS/Search/index';
import { feature, features } from "@/utils/feature-verifier";
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index.jsx'

export default function Eclips({
	defaultSelectedPackages = []
}) {

	/*=================================================
		General
	/================================================*/

	const pageLimit = 10;
	const maxDownloadSize = 150;
	const { addToastMessage } = useContext(ToastContext)
	const { folderId, type } = useParams()
	const { currentState } = useLocation();

	const [isTableView, setIsTableView] = useState(false)
	const [refIdSearch, setRefIdSearch] = useState('')
	const [dataElement, setDataElement] = useState({})
	const [participant, setParticipant] = useState({})
	const [creator, setCreator] = useState({})
	const [status, setStatus] = useState({})
	const [fromDate, setFromDate] = useState('')
	const [endDate, setEndDate] = useState('')
	const [caseOwner, setCaseOwner] = useState('')
	const [refreshData, setRefreshData] = useState(false)

	const { visible: isDownloadAllModalVisible, hide: hideDownloadAllModal, show: showDownloadAllModal } = useModal();
	const [selectedPackages, setSelectedPackages] = useState(defaultSelectedPackages)
	const [fileSize, setFileSize] = useState("");
	const [packageActivity, setPackageActivity] = useState("")

	/*=================================================
		Hooks
	/================================================*/

	const { getFolder, currentFolder, folderErrorMsg } = usePackageFolders()

	const {
		packages,
		fetchAllPackages,
		packageLoading,
		packagesMeta,
		setPackageLoading,
		packageErrorMsg,
		getFilterOptions,
		filterDataElement: folderPackageDataElement,
		filterParticipant: folderPackageParticipant,
		filterStatus: folderPackageStatus,
		filterCreator: folderPackageCreator
	} = usePackages()

	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({
		title: currentFolder?.name,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Package Sharing',
				link: `/package`
			}
		]
	})

	const {
		itemsPerPage: packageItemPerPage,
		currentPage: currentPagePackages,
		goToPrev: goToPrevPage,
		goToNext: goToNextPage,
		handleCurrnetInput: handleCurrentPageInput,
		pageLabel: pageLabelPackages,
		sortConfig: sortConfigPackages,
		setSortConfig: setSortConfigPackages,
	} = usePaginating(
		pageLimit,
		packagesMeta?.total,
		packagesMeta?.totalPages,
	)

	const handleClearFilter = () => {
		setRefIdSearch("")
		setDataElement({})
		setParticipant({})
		setCreator({})
		setStatus({})
		setFromDate("")
		setEndDate("")
		setCaseOwner("")
	}

	const init = () => {
		fetchAllPackages({
			folder_id: folderId,
			sort_by: sortConfigPackages.key,
			sort_order: sortConfigPackages.direction ? 'ASC' : 'DESC',
			data_element_ids: dataElement?.dataElementId || undefined,
			participants: participant?.orgId || undefined,
			reference_number: refIdSearch || undefined,
			status: status?.id || undefined,
			limit: packageItemPerPage,
			page: currentPagePackages,
			from_date: fromDate ? fromDate.toISOString() : undefined,
			to_date: endDate ? endDate.toISOString() : undefined,
			createdByOrg: creator?.orgId || undefined,
			// send_owner: params?.type === 'RECEIVE' ? undefined : caseOwner?.email || undefined,
			// receive_owner: params?.type === 'RECEIVE' ? caseOwner?.email || undefined : undefined,
		})
		getFilterOptions({ folderId: folderId })
		setIsTableView(localStorage.getItem('isTableView') === 'true')
	}

	useEffect(() => {
		getFolder(folderId)
	}, [folderId])


	useEffect(() => {
		setBreadcrumbsItems(
			[
				{
					label: '',
					link: `/`,
				},
				{
					label: 'Package Sharing',
					link: `/package`
				},
				{
					label: `${currentFolder?.name ? currentFolder?.name : ""}`,
					link: ``
				}
			]
		)
	}, [currentFolder?.name])

	useEffect(init, [
		sortConfigPackages.direction,
		folderId,
		type,
		dataElement,
		participant,
		creator,
		refIdSearch,
		status,
		currentPagePackages,
		fromDate,
		endDate,
		caseOwner,
		refreshData
	])

	useEffect(() => {
		if (folderErrorMsg) {
			addToastMessage(folderErrorMsg, 'error')
		}
	}, [folderErrorMsg])

	useEffect(() => {
		if (packageErrorMsg) {
			addToastMessage(packageErrorMsg, 'error')
		}
	}, [packageErrorMsg])

	useEffect(() => {
		if (selectedPackages.length) {
			findFileSize();
		}
		else {
			setFileSize(0)
		}
	}, [selectedPackages])

	async function findFileSize() {
		const payload = {
			ids: selectedPackages
		}

		const [err, response] = await api.checkDownloadAllFileSize("package", payload);

		if (err) {
			return addToastMessage(
				err?.error?.message || "Unable to retrieve Config",
				"error"
			);
		}

		setFileSize(isNaN(response.totalFileSize) ? "0" : response.totalFileSize);
	}

	function setSelectedPackagesInFolder(id) {
		if (!selectedPackages.includes(id)) {
			const array = [...selectedPackages, id];
			return setSelectedPackages(array)
		} else {
			const array = ([...selectedPackages]);
			array.splice(array.indexOf(id), 1)
			return setSelectedPackages(array);
		}
	}

	function handleAllCheckBoxPackages() {
		const array = packages?.map((item) => item.id);
		if (selectedPackages?.length === packages?.length) {
			return setSelectedPackages([]);
		}
		else {
			return setSelectedPackages(array);
		}
	}

	const handleTableToggle = () => {
		setIsTableView(!isTableView);
		isTableView ? localStorage.setItem("isTableView", "false") : localStorage.setItem("isTableView", "true")
	}

	function renderTableToggle() {
		return (
			<div
				data-testid="toggle-table-view"
				onClick={handleTableToggle}
				onKeyDown={handleTableToggle}
				className={isTableView ? "active" : ""}
			>
				{
					isTableView ?
						<div className="package-lading__layout-view tooltip">
							<span className="tooltiptext__package-landing">
								Grid Layout
							</span>
							<IconGridView />
						</div>
						: <div className="package-lading__layout-view tooltip">
							<span className="tooltiptext__package-landing">
								List Layout
							</span>
							<IconTableView />
						</div>
				}
			</div>
		)
	}

	const handleDownloadSelection = () => {
		setPackageActivity("package");
		showDownloadAllModal();
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<div className="eclip-landing__filter">
				<div hidden onClick={init} onKeyDown={init} id="reload-eclips"></div>
				<div className="title-header">
					<div className="title-header__left">
						<div className="folder__header">
							{currentFolder.name} ({packagesMeta?.total || 0})
						</div>
					</div>
					{(packages?.length > 0 ) && <div className="title-header__right">
						<Button
							data-testid="dowload-folder"
							prefix={<IconDownloadFolder />}
							round full
							maxWidth={170}
							text={"Download Folder"}
							submit={() => {
								setPackageActivity("folder"); showDownloadAllModal();
							}}
						/>
					</div>}
				</div>
				<div className="eclip-filter">
					<div className="search-data-input ">
						<div style={{width:"350px"}}>
							<Search
								value={refIdSearch}
								placeholder="Search by Reference ID"
								onChange={(e) => setRefIdSearch(e?.target.value)}
								onCancel={() => setRefIdSearch('')}
							/>
						</div>

						<FilterDropDown
							setFromDate={setFromDate}
							setEndDate={setEndDate}
							dataElementsFilterList={folderPackageDataElement}
							setDataElement={setDataElement}
							participantsFilterList={folderPackageParticipant}
							setParticipant={setParticipant}
							statusFilterList={folderPackageStatus}
							setStatus={setStatus}
							creatorFilterList={folderPackageCreator}
							setCreator={setCreator}
							handleClearFilter={handleClearFilter}
						/>
					</div>
					<div className="eclip-landing__view-switch">
						{!isTableView && (
							<div className="search-data-input__sort-by">
								<span className="sort-by">Sort by</span>
								<Dropdown
									value={
										sortByOptions.find(
											(option) =>
												option.direction ===
												sortConfigPackages.direction
										)?.name
									}
									placeholder="Sort By"
									style={{ width: rem(130), zIndex: 1 }}
								>
									{sortByOptions.map((option) => {
										return (
											<Dropdown.Item key={option.id}
												id={option.key}
												onClick={() =>
													setSortConfigPackages({
														key: option.key,
														direction: option.direction,
													})
												}
											>
												{option.name}
											</Dropdown.Item>
										);
									})}
								</Dropdown>
							</div>
						)}
						{renderTableToggle()}
					</div>
				</div>
			</div>

			{(packages?.length > 0) && (
				<div className="download-all__package-header">
					<div className='download-all__package-details'>
						{selectedPackages?.length > 0 && <div className='download-all__package-details__packages-selected'>{selectedPackages.length} Package(s) Selected ({fileSize} MB)</div>}
						{fileSize > maxDownloadSize && <div className='download-all__package-details__error'><IconDownloadAllError /> *Selection exceeded download size limit (150 MB)*</div>}
					</div>

					<div className='download-all__package-buttons'>
						{selectedPackages?.length > 0 &&
							<div className={(selectedPackages?.length > 0 && fileSize < maxDownloadSize) ? 'download-all__package-details__download-selection' : 'download-all__package-details__download-selection__disabled'}
								onClick={handleDownloadSelection}
								onKeyDown={handleDownloadSelection}
							>
								<div className="download-all_download-selection-icon"><IconDownload /></div>
								<div>Download Selection</div>
							</div>
						}
						{!isTableView && <div className='download-all__package-details__selection' onClick={() => { handleAllCheckBoxPackages() }} onKeyDown={() => { handleAllCheckBoxPackages() }} role='button'>Select/Deselect All</div>}
					</div>
				</div>
			)}

			{isTableView ? (
				<PackageDataTable
					items={packages}
					sortConfig={sortConfigPackages}
					setLoading={setPackageLoading}
					setSortConfig={setSortConfigPackages}
					refreshData={refreshData}
					setRefreshData={setRefreshData}
					folderId={folderId}
					isVeridaptFolder={currentState.isVeridaptFolder}
					folderName={currentFolder?.name}
					selectedPackages={selectedPackages}
					setSelectedPackagesInFolder={setSelectedPackagesInFolder}
					showCheckBox={true}
					handleAllCheckBoxPackages={handleAllCheckBoxPackages}
				/>
			) : (
				<div >
					{packages?.length > 0 ? (
						<div className="folder__package-card--wrapper">
							{packages?.map((item) => {
								return (
									<PackageCard
										key={item.id}
										item={item}
										refreshData={refreshData}
										setRefreshData={setRefreshData}
										folderId={folderId}
										folderName={currentFolder?.name}
										selectedPackages={selectedPackages}
										setSelectedPackagesInFolder={setSelectedPackagesInFolder}
										showCheckBox={true}
									/>
								);
							})}
						</div>
					) : (
						<div className="data-table__table__body folder__wrapper">
							<div className="data-table__empty">
								<div className="data-table__empty__icon">
									<IconEclip />
									You haven’t created any Packages.
								</div>
							</div>
						</div>
					)}
				</div>
			)}

			{packagesMeta?.total ? (
				<div className="eclip-landing__pagination">
					<div className="data-table__table__footer__row">
						<div className="data-table__table__footer__row__left">
							<div className="data-table__table__meta">
								Showing {pageLabelPackages} of{" "}
								{packagesMeta?.total || 0} entries
							</div>
						</div>
						<div className="data-table__table__footer__row__right">
							<div className="data-table__table__pagination">
								<input
									className="data-table__table__pagination__page"
									type="number"
									value={currentPagePackages}
									onChange={(e) =>
										handleCurrentPageInput(e)
									}
									min="1"
								/>
								<span className="data-table__table__pagination__page__meta">
									{" "}
									of{" "}
									{packagesMeta?.totalPages ||
										1}{" "}
									pages{" "}
								</span>
								<button
									className="data-table__table__pagination__prev"
									onClick={() => {
										goToPrevPage()
										setSelectedPackages([])
									}}
									disabled={
										currentPagePackages ===
										1 ||
										!packagesMeta?.totalPages
									}
								>
									<IconChevronDown />
								</button>
								<button
									className="data-table__table__pagination__next"
									onClick={() => {
										goToNextPage()
										setSelectedPackages([])
									}}
									disabled={
										currentPagePackages ===
										packagesMeta?.totalPages ||
										!packagesMeta?.totalPages
									}
								>
									<IconChevronDown />
								</button>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className='folder__wrapper'></div>
			)}

			{packageLoading &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src="/loading.gif" />
						</div>
					</div>,
					document.body
				)
			}

			<DownloadAllDataModal
				visible={isDownloadAllModalVisible}
				close={hideDownloadAllModal}
				all={true}
				isFolder={packageActivity === "folder"}
				downloadName={(packageActivity === "folder" || selectedPackages.length > 1) ? currentFolder?.name : packages?.find((item) => item.id === selectedPackages[0])?.name}
				packageActivity={packageActivity}
				downloadId={packageActivity === "folder" ? [currentFolder?.id] : selectedPackages}
				isVeridapt={currentFolder?.isVeridapt}
			/>
		</>
	);
}

Eclips.propTypes = {
	defaultSelectedPackages: PropTypes.array,
};
